import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "styled-components";
import CloudOffIcon from "icons/CloudOffIcon";
import Modal from "../../UI/PopupModal";
import { FullRefIcon, PlaceHolder } from "./assets";
import { fetchPassage } from "../../../api/BibleApi";
import { chapterPath } from "../../../utils/paths";
import { useNetworkStatus } from "utils/hooks";
import { useFums } from "utils/fums";

interface ScriptureRefModalProps {
  bibleId: string;
  refId: string;
  onClose: () => void;
}

interface PassagePartial {
  reference: string;
  content: string;
  fumsToken: string;
}

const ScriptureRefModal = ({
  bibleId,
  refId,
  onClose
}: ScriptureRefModalProps) => {
  const [passage, setPassage] = useState<PassagePartial>({
    reference: "",
    content: "",
    fumsToken: ""
  });
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState("");
  const theme = useContext(ThemeContext);
  const isOnline = useNetworkStatus();
  const location = useLocation();

  useEffect(() => {
    if (!isOnline) return;

    setLoading(true);
    fetchPassage({ bibleId, passageId: refId })
      .then(passage => {
        const { reference, content, fumsToken } = passage;
        setPassage({ reference, content: content.trim(), fumsToken });
      })
      .catch(err => {
        console.error(err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [bibleId, refId, isOnline]);

  useFums(passage.fumsToken);

  const refLink = chapterPath({
    bibleId,
    chapterId: refId
      .split(".")
      .slice(0, 2)
      .join("."),
    location,
    passageId: refId
  });

  const footer = (
    <Link to={refLink}>
      <FullRefIcon fill={theme.primaryText} />
    </Link>
  );

  if (isError || isLoading) {
    return (
      <Modal onClose={onClose} footer={footer}>
        <PlaceHolder isError={isError} />
      </Modal>
    );
  }

  const { reference, content } = passage;

  if (!isOnline && !reference && !content) {
    return (
      <Modal onClose={onClose} footer={footer}>
        <div style={{ textAlign: "center", margin: "24px 0" }}>
          <CloudOffIcon
            size={128}
            color={theme.darkMode ? "#666" : "#e8e8e8"}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={reference} footer={footer} onClose={onClose}>
      {content}
    </Modal>
  );
};

export default ScriptureRefModal;
